import { useInfiniteQuery } from '@tanstack/react-query';

import { formateWithMoment } from '@/utilz/dates';
import { useGetSelectedCompanyId } from '@/views/screens/Claims/hooks/use-get-selected-company-id';
import type { LossAndDamageFilterType, LossAndDamageSearchFilters } from '@/views/screens/Claims/types';

import { LOSS_AND_DAMAGE_FILTER_TYPES } from '../../constants';
import type { ClaimsListAdvancedRequestParams, ClaimsListBasicRequestParams } from '../data-provider/claims-list';
import { fetchClaimsListAdvanced, fetchClaimsListBasic } from '../data-provider/claims-list';

export interface UseGetClaimsListParams {
  filters: LossAndDamageSearchFilters;
  filterType: LossAndDamageFilterType;
  enabled?: boolean;
}

export const useGetClaimsList = ({ filters, filterType, enabled = true }: UseGetClaimsListParams) => {
  const companyId = useGetSelectedCompanyId();

  return useInfiniteQuery({
    enabled: enabled && Boolean(companyId),
    getNextPageParam: (lastPage: any) => lastPage?.nextPage,
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      const claimStatus = filters.claimStatus?.join(',') || '';
      const pageNo = Number(pageParam);

      if (filterType === LOSS_AND_DAMAGE_FILTER_TYPES.BASIC) {
        const params: ClaimsListBasicRequestParams = {
          ...filters,
          claimStatus,
          daysOld: filters.daysOld || undefined,
          pageNo,
        };
        return fetchClaimsListBasic(params);
      }
      const startDate = filters.startDate ? formateWithMoment(filters.startDate, 'YYYY-MM-DD') : undefined;
      const endDate = filters.endDate ? formateWithMoment(filters.endDate, 'YYYY-MM-DD') : undefined;
      const params: ClaimsListAdvancedRequestParams = {
        ...filters,
        claimStatus,
        endDate,
        pageNo,
        startDate,
      };
      return fetchClaimsListAdvanced(params);
    },
    queryKey: ['claims-loss-and-damage-list', companyId, filterType, filters],
    select: (data) => {
      const flatClaims = data.pages.flatMap((page) => page.claimsData ?? []);
      const lastPage = data.pages[data.pages.length - 1];

      return {
        ...data,
        flatClaims,
        footerData: lastPage?.footerData ?? null,
        totalCount: Number(lastPage?.footerData?.totalClaim ?? 0),
      };
    },
  });
};
